function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import Log from '@deephaven/log';
import { assertNotNull, ColorUtils, requestParentResponse } from '@deephaven/utils';
import { themeDark } from "./theme-dark/index.js";
import { themeLight } from "./theme-light/index.js";
import { DEFAULT_DARK_THEME_KEY, DEFAULT_LIGHT_THEME_KEY, DEFAULT_PRELOAD_DATA_VARIABLES, THEME_CACHE_LOCAL_STORAGE_KEY, SVG_ICON_MANUAL_COLOR_MAP, THEME_KEY_OVERRIDE_QUERY_PARAM, EXTERNAL_THEME_KEY, MSG_REQUEST_GET_THEME, TRANSPARENT_PRELOAD_DATA_VARIABLES, PRELOAD_TRANSPARENT_THEME_QUERY_PARAM } from "./ThemeModel.js";
var log = Log.module('ThemeUtils');
export var CSS_VAR_EXPRESSION_PREFIX = 'var(--';
export var DH_VAR_PREFIX = '--dh-color-';
export var TMP_CSS_PROP_PREFIX = 'dh-tmp';
export var NON_WHITESPACE_REGEX = /\S/;
export var WHITESPACE_REGEX = /\s/;
export var DH_CSS_VAR_NAME_REGEXP = /^--dh-color-[a-z0-9_-]+$/;
/**
 * Resolves the current values of CSS variables we want to preload. Preloading
 * happens before themes are fully loaded so that we can style things like the
 * loading spinner and background color which are shown to the user early on in
 * the app lifecycle.
 * @defaultPreloadValues Default values to use if a preload variable is not set.
 */
export function calculatePreloadStyleContent(defaultPreloadValues) {
  var resolveVar = createCssVariableResolver(document.body, defaultPreloadValues);

  // Calculate the current preload variables. If the variable is not set, use
  // the default value.
  var pairs = Object.keys(defaultPreloadValues).map(key => "".concat(key, ":").concat(resolveVar(key)));
  return ":root{".concat(pairs.join(';'), "}");
}

/**
 * Create a resolver function for calculating the value of a css variable based
 * on a given element's computed style. If the variable resolves to '', we check
 * `defaultValues` for a default value, and if one does not exist,
 * return ''.
 * @param el Element to resolve css variables against
 * @param defaultValues Default values to use if a variable is not set.
 */
export function createCssVariableResolver(el, defaultValues) {
  var computedStyle = getComputedStyle(el);

  /**
   * Resolve the given css variable name to a value. If the variable is not set,
   * return the default preload value or '' if one does not exist.
   */
  return function cssVariableResolver(varName) {
    var _defaultValues;
    var value = computedStyle.getPropertyValue(varName);
    if (value !== '') {
      return value;
    }
    return (_defaultValues = defaultValues[varName]) !== null && _defaultValues !== void 0 ? _defaultValues : '';
  };
}

/**
 * Create a style tag containing preload css variables and add to the head.
 * @param id The id of the style tag
 * @param preloadStyleContent The css variable content to add to the style tag
 */
export function createPreloadStyleElement(id, preloadStyleContent) {
  var style = document.createElement('style');
  style.id = id;
  style.innerHTML = preloadStyleContent;
  document.head.appendChild(style);
}

/**
 * Extracts all css variable expressions from the given record and returns
 * a set of unique expressions.
 * @param record The record to extract css variable expressions from
 */
export function extractDistinctCssVariableExpressions(record) {
  var set = new Set();
  Object.values(record).forEach(value => {
    getExpressionRanges(value).forEach(_ref => {
      var _ref2 = _slicedToArray(_ref, 2),
        start = _ref2[0],
        end = _ref2[1];
      var expression = value.substring(start, end + 1);
      if (expression.includes(CSS_VAR_EXPRESSION_PREFIX)) {
        set.add(expression);
      }
    });
  });
  return set;
}

/**
 * Returns an array of the active themes. The first item will always be one
 * of the base themes. Optionally, the second item will be a custom theme.
 */
export function getActiveThemes(themeKey, themeRegistration) {
  var _custom$baseThemeKey;
  var custom = themeRegistration.custom.find(theme => theme.themeKey === themeKey);
  var baseThemeKey = (_custom$baseThemeKey = custom === null || custom === void 0 ? void 0 : custom.baseThemeKey) !== null && _custom$baseThemeKey !== void 0 ? _custom$baseThemeKey : themeKey;
  var base = themeRegistration.base.find(theme => theme.themeKey === baseThemeKey);
  if (base == null) {
    log.error("No registered base theme found for theme key: '".concat(baseThemeKey, "'"), 'Registered:', themeRegistration.base.map(theme => theme.themeKey), themeRegistration.custom.map(theme => theme.themeKey));
    base = themeRegistration.base.find(theme => theme.themeKey === DEFAULT_DARK_THEME_KEY);
    assertNotNull(base, "Default base theme '".concat(DEFAULT_DARK_THEME_KEY, "' is not registered"));
  }
  log.debug('Applied themes:', base.themeKey, custom === null || custom === void 0 ? void 0 : custom.themeKey);
  return custom == null ? [base] : [base, custom];
}

/**
 * Get default base theme data.
 */
export function getDefaultBaseThemes() {
  return [{
    name: 'Default Dark',
    themeKey: DEFAULT_DARK_THEME_KEY,
    styleContent: themeDark
  },
  // The ThemePicker shows whenever more than 1 theme is available. Disable
  // light theme for now to keep the picker hidden until it is fully
  // implemented by #1539.
  {
    name: 'Default Light',
    themeKey: DEFAULT_LIGHT_THEME_KEY,
    styleContent: themeLight
  }];
}

/**
 * Get the default selected theme key. Precedence is:
 * 1. Theme key override query parameter
 * 2. Theme key from preload data
 * 3. Default dark theme key
 * @returns The default selected theme key
 */
export function getDefaultSelectedThemeKey() {
  var _ref3, _getThemeKeyOverride, _getThemePreloadData;
  return (_ref3 = (_getThemeKeyOverride = getThemeKeyOverride()) !== null && _getThemeKeyOverride !== void 0 ? _getThemeKeyOverride : (_getThemePreloadData = getThemePreloadData()) === null || _getThemePreloadData === void 0 ? void 0 : _getThemePreloadData.themeKey) !== null && _ref3 !== void 0 ? _ref3 : DEFAULT_DARK_THEME_KEY;
}

/**
 * Derive unique theme key from plugin root path and theme name.
 * @param pluginName The root path of the plugin
 * @param themeName The name of the theme
 */
export function getThemeKey(pluginName, themeName) {
  return "".concat(pluginName, "_").concat(themeName);
}

/**
 * A theme key override can be set via a query parameter to force a specific
 * theme selection. Useful for embedded widget scenarios that don't expose the
 * theme selector.
 */
export function getThemeKeyOverride() {
  var searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(THEME_KEY_OVERRIDE_QUERY_PARAM);
}

/**
 * Get the preload data from local storage or null if it does not exist or is
 * invalid
 */
export function getThemePreloadData() {
  var data = localStorage.getItem(THEME_CACHE_LOCAL_STORAGE_KEY);
  try {
    return data == null ? null : JSON.parse(data);
  } catch (_unused) {
    // ignore
  }
  return null;
}

/**
 * Identifies start and end indices of any top-level expressions in the given
 * string.
 *
 * e.g.
 * getExpressionRanges('var(--aaa-aa) #fff var(--bbb-bb)')
 * yields:
 * [
 *   [0, 12],  // 'var(--aaa-aa)'
 *   [14, 17]  // '#fff'
 *   [19, 31], // 'var(--bbb-bb)'
 * ]
 *
 * In cases where there are nested expressions, only the indices of the outermost
 * expression will be included.
 *
 * e.g.
 * getExpressionRanges('var(--ccc-cc, var(--aaa-aa, green)) var(--bbb-bb)')
 * yields:
 * [
 *   [0, 34],  // 'var(--ccc-cc, var(--aaa-aa, green))'
 *   [36, 48], // 'var(--bbb-bb)'
 * ]
 * @param value The string to search for expressions
 * @returns An array of [start, end] index pairs for each expression
 */
export function getExpressionRanges(value) {
  var _NON_WHITESPACE_REGEX, _NON_WHITESPACE_REGEX2;
  var ranges = [];
  var start = (_NON_WHITESPACE_REGEX = (_NON_WHITESPACE_REGEX2 = NON_WHITESPACE_REGEX.exec(value)) === null || _NON_WHITESPACE_REGEX2 === void 0 ? void 0 : _NON_WHITESPACE_REGEX2.index) !== null && _NON_WHITESPACE_REGEX !== void 0 ? _NON_WHITESPACE_REGEX : 0;
  var parenLevel = 0;
  for (var i = 0; i < value.length; i += 1) {
    if (value[i] === '(') {
      parenLevel += 1;
    } else if (value[i] === ')') {
      parenLevel -= 1;
    }
    if (i === value.length - 1 || WHITESPACE_REGEX.test(value[i + 1]) && parenLevel === 0) {
      ranges.push([start, i]);
      while (i < value.length - 1 && WHITESPACE_REGEX.test(value[i + 1])) {
        i += 1;
      }
      start = i + 1;
    }
  }
  if (parenLevel !== 0) {
    log.error('Unbalanced parentheses in css var expression', value);
    return [];
  }
  return ranges;
}

/**
 * Check if the given theme key is one of the base themes.
 * @param themeKey The theme key to check
 * @returns True if the theme key is a base theme key, false otherwise
 */
export function isBaseThemeKey(themeKey) {
  return [DEFAULT_DARK_THEME_KEY, DEFAULT_LIGHT_THEME_KEY].includes(themeKey);
}

/**
 * Determine if a given object is a `ExternalThemeData` object.
 * @param maybeExternalThemeData An object that may or may not be a `ExternalThemeData`
 * @returns True if the object is a `ExternalThemeData`, false otherwise
 */
export function isExternalThemeData(maybeExternalThemeData) {
  if (typeof maybeExternalThemeData !== 'object' || maybeExternalThemeData == null) {
    return false;
  }
  return 'name' in maybeExternalThemeData && typeof maybeExternalThemeData.name === 'string' && 'cssVars' in maybeExternalThemeData && typeof maybeExternalThemeData.cssVars === 'object' && maybeExternalThemeData.cssVars != null;
}

/**
 * Check if the current URL specifies an external theme key override.
 * @returns True if the external theme key override is set, false otherwise
 */
export function isExternalThemeEnabled() {
  var searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(THEME_KEY_OVERRIDE_QUERY_PARAM) === EXTERNAL_THEME_KEY;
}

/**
 * Check if PRELOAD_TRANSPARENT_THEME_QUERY_PARAM query parameter is set to true.
 * @returns True if the preload transparent theme query parameter is set, false
 * otherwise
 */
export function isPreloadTransparentTheme() {
  var searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(PRELOAD_TRANSPARENT_THEME_QUERY_PARAM) === 'true';
}

/**
 * Validate that a given CSS variable name / value pair is a valid Deephaven
 * color variable.
 * @param name The name of the CSS variable to validate, e.g. '--dh-color-primary'
 * @param value The value of the CSS color to validate
 * @returns True if the name is a valid Deephaven color variable and the value
 * is a valid CSS color, false otherwise
 */
export function isValidColorVar(name, value) {
  return DH_CSS_VAR_NAME_REGEXP.test(name) && CSS.supports('color', value);
}

/**
 * Parse external theme data into a `ThemeData` object. Invalid CSS color variable
 * pairs are excluded from the resulting `ThemeData` object.
 * @param externalThemeData The external theme data to parse
 * @returns A `ThemeData` object representing the external theme
 */
export function parseExternalThemeData(_ref4) {
  var _ref4$baseThemeKey = _ref4.baseThemeKey,
    baseThemeKey = _ref4$baseThemeKey === void 0 ? DEFAULT_DARK_THEME_KEY : _ref4$baseThemeKey,
    name = _ref4.name,
    cssVars = _ref4.cssVars;
  var toExpression = _ref5 => {
    var _ref6 = _slicedToArray(_ref5, 2),
      varName = _ref6[0],
      varValue = _ref6[1];
    return isValidColorVar(varName, varValue) ? "".concat(varName, ":").concat(varValue, ";") : null;
  };
  var sanitized = Object.entries(cssVars).map(toExpression).filter(str => str != null);
  var styleContent = sanitized.length === 0 ? '' : ":root{".concat(sanitized.join(''), "}");
  return {
    baseThemeKey,
    themeKey: EXTERNAL_THEME_KEY,
    name,
    styleContent
  };
}

/**
 * Replace the `fill='...'` attribute in the given SVG content with the given
 * color string.
 * @param svgContent Inline SVG content to replace the fill color in
 * @param fillColor The color to replace the fill color with
 */
export function replaceSVGFillColor(svgContent, fillColor) {
  return svgContent.replace(/fill='.*?'/, "fill='".concat(encodeURIComponent(fillColor), "'"));
}

/**
 * Request theme data from the parent window.
 * @returns A promise that resolves to the external theme data
 * @throws Error if the response is not a valid `ExternalThemeData`
 */
export function requestExternalThemeData() {
  return _requestExternalThemeData.apply(this, arguments);
}

/**
 * Make a copy of the given object replacing any css variable expressions
 * contained in its prop values with values resolved from the given HTML element.
 * Variables that resolve to color strings will also be normalized to 8 digit
 * hex values (or optionally 6 digit hex if `isAlphaOptional` is true).
 *
 * Note that the browser will force a reflow when calling `getComputedStyle` if
 * css properties have changed. In order to avoid a reflow for every property
 * check we use distinct setup, resolve / normalize, and cleanup passes:
 * 1. Setup - Create a tmp element and set all css props we want to evaluate
 * 2. Resolve / Normalize - Evaluate all css props via `getPropertyValue` calls
 *    and replace the original expressions with resolved values. Also normalize
 *    css colors to rgb/a.
 * 3. Cleanup - Remove the tmp element
 * @param record An object whose values may contain css var expressions
 * @param targetElement The element to resolve css variables against. Defaults
 * to document.body
 * @param isAlphaOptional If true, the alpha value will be dropped from resolved
 * 8 character hex colors if it is 'ff'. Defaults to false.
 */
function _requestExternalThemeData() {
  _requestExternalThemeData = _asyncToGenerator(function* () {
    var result = yield requestParentResponse(MSG_REQUEST_GET_THEME);
    if (!isExternalThemeData(result)) {
      throw new Error("Unexpected external theme data response: ".concat(JSON.stringify(result)));
    }
    return result;
  });
  return _requestExternalThemeData.apply(this, arguments);
}
export function resolveCssVariablesInRecord(record) {
  var targetElement = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : document.body;
  var isAlphaOptional = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var perfStart = performance.now();

  // Add a temporary div to attach temp css variables to
  var tmpPropEl = document.createElement('div');
  tmpPropEl.style.display = 'none';
  var recordArray = Object.entries(record);
  recordArray.forEach((_ref7, i) => {
    var _ref8 = _slicedToArray(_ref7, 2),
      value = _ref8[1];
    tmpPropEl.style.setProperty("--".concat(TMP_CSS_PROP_PREFIX, "-").concat(i), value);
    // faster to create these now all at once, even if we don't use them all
    // since the parent isn't added yet to the DOM
    var el = document.createElement('div');
    // use background color instead of color to avoid inherited values
    el.style.backgroundColor = value;
    tmpPropEl.appendChild(el);
  });

  // append only once to avoid multiple re-layouts
  // must be part of DOM to get computed color
  targetElement.appendChild(tmpPropEl);
  var tempPropElComputedStyle = window.getComputedStyle(tmpPropEl);
  var result = {};
  recordArray.forEach((_ref9, i) => {
    var _ref0 = _slicedToArray(_ref9, 2),
      key = _ref0[0],
      value = _ref0[1];
    // resolves any variables in the expression
    var resolved = tempPropElComputedStyle.getPropertyValue("--".concat(TMP_CSS_PROP_PREFIX, "-").concat(i));
    var containsCssVar = value.includes(CSS_VAR_EXPRESSION_PREFIX);
    var isColor = CSS.supports('color', resolved);
    if (
    // only try to normalize non-hex strings that are valid colors
    // otherwise non-colors will be made #00000000
    isColor && !/^#[0-9A-F]{6}[0-9a-f]{0,2}$/i.test(resolved)) {
      // getting the computed background color is necessary
      // because resolved can still contain a color-mix() function
      var el = tmpPropEl.children[i];
      var computedStyle = window.getComputedStyle(el);
      var color = computedStyle.getPropertyValue('background-color');
      // convert color to hex, which is what monaco and plotly require
      resolved = ColorUtils.normalizeCssColor(color, isAlphaOptional);
    }
    result[key] = containsCssVar || isColor ? resolved : value;
  });

  // Remove the temporary div
  tmpPropEl.remove();
  log.debug('Resolved css variables', performance.now() - perfStart, 'ms');
  return result;
}

/**
 * Resolve css variable expressions in the given string using the
 * given resolver and replace the original expressions with the resolved values.
 *
 * @param resolver Function that can resolve a css variable expression
 * @param value Value that may contain css variable expressions
 */
export function resolveCssVariablesInString(resolver, value) {
  var result = [];
  var i = 0;
  getExpressionRanges(value).forEach(_ref1 => {
    var _ref10 = _slicedToArray(_ref1, 2),
      start = _ref10[0],
      end = _ref10[1];
    if (i < start) {
      result.push(value.substring(i, start));
      i += start - i;
    }
    var expression = value.substring(start, end + 1);
    result.push(expression.includes(CSS_VAR_EXPRESSION_PREFIX) ? resolver(expression) : expression);
    i += end - start + 1;
  });
  if (result.length === 0) {
    return value;
  }
  return result.join('');
}

/**
 * Store theme preload data in local storage.
 * @param preloadData The preload data to set
 */
export function setThemePreloadData(preloadData) {
  localStorage.setItem(THEME_CACHE_LOCAL_STORAGE_KEY, JSON.stringify(preloadData));
}

/**
 * Preload minimal theme variables from the cache.
 * @defaultPreloadValues Optional default values to use if a preload variable is not set.
 */
export function preloadTheme() {
  var _getThemePreloadData2;
  var defaultPreloadValues = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : DEFAULT_PRELOAD_DATA_VARIABLES;
  // In certain cases we may want to preload a transparent theme to allow the
  // parent container to show through. For example, when a parent Window is
  // providing a theme via `postMessage` apis, we may not have enough information
  // to properly preload the theme, so we can just preload a transparent
  // theme and let the parent container show through until `postMessage`
  // communication is complete.
  if (isPreloadTransparentTheme()) {
    createPreloadStyleElement('theme-preload-transparent', calculatePreloadStyleContent(TRANSPARENT_PRELOAD_DATA_VARIABLES));
    return;
  }
  var previousPreloadStyleContent = (_getThemePreloadData2 = getThemePreloadData()) === null || _getThemePreloadData2 === void 0 ? void 0 : _getThemePreloadData2.preloadStyleContent;
  var defaultPreloadStyleContent = calculatePreloadStyleContent(defaultPreloadValues);
  log.debug('Preloading theme content:', {
    defaultPreloadStyleContent,
    previousPreloadStyleContent
  });
  createPreloadStyleElement('theme-preload-defaults', defaultPreloadStyleContent);

  // Any preload variables that were saved by last theme load should override
  // the defaults
  if (previousPreloadStyleContent != null) {
    createPreloadStyleElement('theme-preload-previous', previousPreloadStyleContent);
  }
}

/**
 * Inline SVGs cannot depend on dynamic CSS variables, so we have to statically
 * update them if we want to change their color.
 *
 * This function:
 * 1. Clears any previous overrides
 * 2. Resolves CSS variables containing inline SVG urls
 * 3. Resolves mapped color variables and replaces the `fill='...'` attribute with the result
 * 4. Sets the original CSS variable to the new replaced value
 *
 * Note that it is preferable to use inline SVGs as background-mask values and
 * just change the background color instead of relying on this util, but this
 * is not always possible. e.g. <select> elements don't support pseudo elements,
 * so there's not a good way to set icons via masks.
 * @param defaultValues Default values to use if a variable is not set.
 */
export function overrideSVGFillColors(defaultValues) {
  var resolveVar = createCssVariableResolver(document.body, defaultValues);
  Object.entries(SVG_ICON_MANUAL_COLOR_MAP).forEach(_ref11 => {
    var _ref12 = _slicedToArray(_ref11, 2),
      key = _ref12[0],
      value = _ref12[1];
    // Clear any previous override so that our variables get resolved against the
    // actual svg content provided by the active themes and not from a previous
    // override
    document.body.style.removeProperty(key);
    var svgContent = resolveVar(key);
    var fillColor = resolveVar(value);
    var newSVGContent = replaceSVGFillColor(svgContent, fillColor);

    // This will take precedence over any values for the variable provided by
    // the active themes
    document.body.style.setProperty(key, newSVGContent);
  });
}
//# sourceMappingURL=ThemeUtils.js.map